/**
 * AAA-ITEM3: orbital-lines HUD overlay (toggleable, default OFF).
 *
 * Renders each orbiting body's full elliptical path as a closed loop by sampling
 * 128 steps across one full period using sim-core `propagate` (no Kepler
 * re-implementation client-side).  Child-body loops are centred on the PARENT's
 * current world position; planet loops centre on Sol / origin.
 *
 * Also draws a small cross marker at each STATION's live world position (moves
 * with the host body) and at each DERELICT signal source's fixed position.
 *
 * Pattern mirrors SpeedLines: zero-alloc THREE.LineSegments, Float32Array written
 * every frame, floating-origin (subtract ship world pos each frame), no new
 * geometry allocated at runtime.
 *
 * Aesthetics: low-opacity cyan (~0.3 alpha), additive blending, depthWrite:false.
 */
import * as THREE from 'three';
import { propagate, type Vec3, type StationSpec } from '@blackmarble/sim-core';
import type { World } from '../sim/world.js';
import type { SignalSource } from '@blackmarble/sim-core';

const ORBIT_SEGMENTS = 128; // points per orbit loop (closed = 128 line segments)
const STATION_MARK_KM = 60; // half-length of the station cross arms
const DERELICT_MARK_KM = 40;

/**
 * Each orbit loop is ORBIT_SEGMENTS line segments (2 verts each).
 * We allocate one geometry per body; vertices are mutated every frame.
 * Station markers are 3-segment crosses (6 line pairs => 12 verts each axis, 6
 * for a 2D cross in the orbital plane). For simplicity we render each cross as 2
 * line segments (4 verts) = 1 segment horizontal + 1 segment vertical.
 */
export class OrbitLines {
  private visible = false;

  /** One LineSegments object per orbital body (planets + moons, NOT Sol). */
  private readonly loops: {
    segments: THREE.LineSegments;
    positions: Float32Array;
    geo: THREE.BufferGeometry;
  }[] = [];

  /** Station cross markers (2 segments each = 4 verts). */
  private readonly stationMarkers: {
    segments: THREE.LineSegments;
    positions: Float32Array;
    geo: THREE.BufferGeometry;
    spec: StationSpec;
  }[] = [];

  /** Derelict markers (fixed positions; updated each frame for floating-origin). */
  private readonly derelictMarkers: {
    segments: THREE.LineSegments;
    positions: Float32Array;
    geo: THREE.BufferGeometry;
  }[] = [];

  private derelictSignals: readonly SignalSource[] = [];

  /**
   * Text-label sprites — one per orbiting body + one per station.
   * Each entry carries the sprite and a resolver that returns its live
   * world position given a byName map (built each update() call).
   */
  private readonly labelSprites: {
    sprite: THREE.Sprite;
    resolve: (byName: Map<string, { worldPos: Vec3 }>) => Vec3 | null;
  }[] = [];

  constructor(
    private readonly parent: THREE.Group,
    private readonly world: World,
    stations: readonly StationSpec[],
  ) {
    const mat = new THREE.LineBasicMaterial({
      color: 0x3fd0e0,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    // Build one loop per orbiting body.
    for (const body of world.bodies) {
      if (!body.entry.orbit || !body.entry.parent) continue; // Sol: skip
      const nVerts = ORBIT_SEGMENTS * 2; // 2 endpoints per segment
      const positions = new Float32Array(nVerts * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const seg = new THREE.LineSegments(geo, mat.clone());
      seg.frustumCulled = false;
      seg.renderOrder = 1;
      seg.visible = false;
      parent.add(seg);
      this.loops.push({ segments: seg, positions, geo });
    }

    // Station cross markers.
    const stationMat = new THREE.LineBasicMaterial({
      color: 0x3fd0e0,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    for (const spec of stations) {
      const positions = new Float32Array(4 * 3); // 2 segments × 2 verts × 3 floats
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const seg = new THREE.LineSegments(geo, stationMat.clone());
      seg.frustumCulled = false;
      seg.renderOrder = 1;
      seg.visible = false;
      parent.add(seg);
      this.stationMarkers.push({ segments: seg, positions, geo, spec });
    }

    // Pre-build label sprites — one per orbiting body, one per station.
    for (const body of world.bodies) {
      if (!body.entry.orbit || !body.entry.parent) continue;
      const name = body.entry.name;
      const sprite = makeLabelSprite(name);
      sprite.visible = false;
      parent.add(sprite);
      this.labelSprites.push({
        sprite,
        resolve: (byName) => byName.get(name)?.worldPos ?? null,
      });
    }
    for (const spec of stations) {
      const sprite = makeLabelSprite(spec.id);
      sprite.visible = false;
      parent.add(sprite);
      const hostName = spec.hostBody;
      const offset = spec.offset;
      this.labelSprites.push({
        sprite,
        resolve: (byName) => {
          const host = byName.get(hostName);
          if (!host) return null;
          return {
            x: host.worldPos.x + offset.x,
            y: host.worldPos.y + offset.y,
            z: host.worldPos.z + offset.z,
          };
        },
      });
    }
  }

  /** Toggle visibility on/off. */
  toggle(): void {
    this.visible = !this.visible;
    if (!this.visible) {
      for (const l of this.loops) l.segments.visible = false;
      for (const m of this.stationMarkers) m.segments.visible = false;
      for (const d of this.derelictMarkers) d.segments.visible = false;
      for (const lb of this.labelSprites) lb.sprite.visible = false;
    }
  }

  get isVisible(): boolean {
    return this.visible;
  }

  /** Set the live derelict signal list each frame (may change as signals spawn/die). */
  setDerelicts(signals: readonly SignalSource[]): void {
    const derelicts = signals.filter((s) => s.kind === 'derelict');
    // Grow pool if new derelicts appeared.
    const derelictMat = new THREE.LineBasicMaterial({
      color: 0x9b8f7a,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    while (this.derelictMarkers.length < derelicts.length) {
      const positions = new Float32Array(4 * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const seg = new THREE.LineSegments(geo, derelictMat.clone());
      seg.frustumCulled = false;
      seg.renderOrder = 1;
      seg.visible = false;
      this.parent.add(seg);
      this.derelictMarkers.push({ segments: seg, positions, geo });
    }
    this.derelictSignals = derelicts;
  }

  /**
   * Update every loop + marker.  Called once per render frame.
   * @param shipWorldPos  ship's Float64 world position (floating-origin anchor)
   * @param simTime       current sim time (seconds) — used to position parent bodies
   */
  update(shipWorldPos: Vec3, simTime: number): void {
    if (!this.visible) return;

    const bodies = this.world.bodies;
    const byName = new Map(bodies.map((b) => [b.entry.name, b]));

    let loopIdx = 0;
    for (const body of bodies) {
      const { entry } = body;
      if (!entry.orbit || !entry.parent) continue; // Sol: skip

      const loop = this.loops[loopIdx++];
      if (!loop) continue;

      // Parent's current world position (already computed by world.update).
      const parentBody = byName.get(entry.parent);
      const parentWP: Vec3 = parentBody ? parentBody.worldPos : { x: 0, y: 0, z: 0 };

      // Derive the parent GM for mean-motion from the body's own period.
      // propagate() accepts gmParent but respects el.period when > 0, so we can
      // pass 0 safely for bodies that have an explicit period baked in.
      const gmParent = 0; // period-based propagation (all our manifest bodies use explicit period)

      const pos = loop.positions;
      const period = entry.orbit.period;
      const step = period / ORBIT_SEGMENTS;

      for (let k = 0; k < ORBIT_SEGMENTS; k++) {
        const t0 = simTime + k * step;
        const t1 = simTime + ((k + 1) % ORBIT_SEGMENTS) * step;
        const p0 = propagate(entry.orbit, gmParent, t0);
        const p1 = propagate(entry.orbit, gmParent, t1);

        // Floating-origin: world pos = parentWP + relative; subtract ship.
        const o = k * 6;
        pos[o]     = (parentWP.x + p0.x) - shipWorldPos.x;
        pos[o + 1] = (parentWP.y + p0.y) - shipWorldPos.y;
        pos[o + 2] = (parentWP.z + p0.z) - shipWorldPos.z;
        pos[o + 3] = (parentWP.x + p1.x) - shipWorldPos.x;
        pos[o + 4] = (parentWP.y + p1.y) - shipWorldPos.y;
        pos[o + 5] = (parentWP.z + p1.z) - shipWorldPos.z;
      }

      (loop.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      loop.segments.visible = true;
    }

    // Station markers.
    for (const marker of this.stationMarkers) {
      const hostBody = byName.get(marker.spec.hostBody);
      const hostWP: Vec3 = hostBody ? hostBody.worldPos : { x: 0, y: 0, z: 0 };
      const wx = hostWP.x + marker.spec.offset.x - shipWorldPos.x;
      const wy = hostWP.y + marker.spec.offset.y - shipWorldPos.y;
      const wz = hostWP.z + marker.spec.offset.z - shipWorldPos.z;
      writeCross(marker.positions, wx, wy, wz, STATION_MARK_KM);
      (marker.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      marker.segments.visible = true;
    }

    // Derelict markers.
    const derelicts = this.derelictSignals;
    for (let di = 0; di < this.derelictMarkers.length; di++) {
      const dm = this.derelictMarkers[di]!;
      const sig = derelicts[di];
      if (!sig) {
        dm.segments.visible = false;
        continue;
      }
      const wx = sig.pos.x - shipWorldPos.x;
      const wy = sig.pos.y - shipWorldPos.y;
      const wz = sig.pos.z - shipWorldPos.z;
      writeCross(dm.positions, wx, wy, wz, DERELICT_MARK_KM);
      (dm.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      dm.segments.visible = true;
    }

    // Text labels — position each sprite at its body/station floating-origin loc.
    positionLabels(this.labelSprites, byName, shipWorldPos);
  }
}

// ---------------------------------------------------------------------------
// Label-sprite helpers (no per-frame allocation — only reuse existing objects)
// ---------------------------------------------------------------------------

/** Build one canvas-based text Sprite for `text`. sizeAttenuation:false keeps
 *  the label legible at any distance without growing to fill the screen. */
function makeLabelSprite(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 48;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, 256, 48);
  ctx.font = 'bold 20px "Courier New", monospace';
  ctx.fillStyle = 'rgba(63,208,224,0.9)'; // match orbit-line cyan
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 8, 24);
  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    sizeAttenuation: false, // fixed screen-size: stays readable at any range
    blending: THREE.AdditiveBlending,
  });
  const sprite = new THREE.Sprite(mat);
  // Scale: sizeAttenuation:false units are fraction of viewport height; 0.06 is legible.
  sprite.scale.set(0.12, 0.03, 1);
  sprite.frustumCulled = false;
  sprite.renderOrder = 2;
  return sprite;
}

type LabelEntry = {
  sprite: THREE.Sprite;
  resolve: (byName: Map<string, { worldPos: Vec3 }>) => Vec3 | null;
};

/** Position each label sprite at its floating-origin world location and show it.
 *  Extracted so update() stays well under 50 lines. */
function positionLabels(
  labels: readonly LabelEntry[],
  byName: Map<string, { worldPos: Vec3 }>,
  shipWorldPos: Vec3,
): void {
  for (const lb of labels) {
    const wp = lb.resolve(byName);
    if (!wp) {
      lb.sprite.visible = false;
      continue;
    }
    lb.sprite.position.set(
      wp.x - shipWorldPos.x,
      wp.y - shipWorldPos.y,
      wp.z - shipWorldPos.z,
    );
    lb.sprite.visible = true;
  }
}

/** Write a 2-segment cross (XZ plane) centred at (cx,cy,cz) into a 12-float buffer. */
const writeCross = (
  pos: Float32Array,
  cx: number,
  cy: number,
  cz: number,
  half: number,
): void => {
  // Segment 0: X axis
  pos[0] = cx - half; pos[1] = cy; pos[2] = cz;
  pos[3] = cx + half; pos[4] = cy; pos[5] = cz;
  // Segment 1: Z axis
  pos[6] = cx; pos[7] = cy; pos[8] = cz - half;
  pos[9] = cx; pos[10] = cy; pos[11] = cz + half;
};
